import { Button, Spinner, useLoadingTimeout, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PostMinimal } from '../posts';
import { usePhotoUrl } from '../posts';
import type { SessionItem } from '../sessions';
import { IdeaDetailModal } from './IdeaDetailModal';
import { useEcwIdeas, type IdeaMinimal } from './useEcwIdeas';
import { useEcwInsights, type InsightMinimal } from './useEcwInsights';
import { useEcwOutbox } from './useEcwOutbox';

/** Task C: outbox 由来の楽観 UI 表示用 internal alias (型汚染回避)。 */
type IdeaWithOutbox = IdeaMinimal & { _pendingOutbox?: boolean };

/**
 * 分析アプリ「処方箋」タブ (Sprint X5c minimum、REQ-4.2.2 / R-38 #3、旧 `IdeaCardList.tsx` 872 行 + `IdeaForm.tsx` 1364 行から narrow 移植)。
 *
 * minimum 仕様 (旧 IdeaForm の核):
 *   - 一覧表示 (createdAt 降順)
 *   - 作成 modal (title / observations / actionIdea / insightIds / selectedPostIds)
 *   - 削除 button (作成者本人のみ表示、rules で enforce)
 *   - insight 多対多紐付け (チェックボックス)
 *
 * 旧 IdeaForm との差分:
 *   - AI 対話 (Step3-4 + llmActionQuestions / llmEffectQuestions / llmInsightSummary) は scope 外
 *   - referenceImageBase64 (参考画像) は scope 外
 *   - emotionTag / categoryCode 自動継承 は scope 外
 *   - team / realName 入力は scope 外
 */

export interface AnalysisIdeaViewProps {
  districtId: string;
  uid: string | null;
  /** R-70: 「自分の処方箋」とみなす全 uid (現 uid + 端末引き継ぎ元)。省略時は uid 単独。 */
  selfUids?: string[];
  nickname: string;
  posts: PostMinimal[];
  onSelectPost?: (post: PostMinimal) => void;
  /**
   * 当該地区の全セッション (id+name)。カードのセッション名表示 + セッションフィルタの
   * 有効 ID 判定に使う。未指定ならセッション表示/絞込なし。
   */
  sessions?: SessionItem[];
  /**
   * ヘッダー右上のセッションフィルタの選択集合。処方箋も投稿と同じ規則で絞り込む
   * (未紐付け / 孤児 ID は常に表示、空集合は「絞込なし」)。未指定なら絞込なし。
   */
  selectedSessionIds?: Set<string>;
  /** M4 aiDialogueSettings ID (Sprint X5g、未指定なら callable 側で 'ecw-pilot' fallback)。 */
  aiDialogueSettingsId?: string | null;
  /**
   * Sprint BG-DataSettings 23 — InsightCard → IdeaWorkspace の handoff。
   * Phase B: 受信時に親 AnalysisPage が `screen='ideaForm' mode='back'` に切替えるため、
   * 本 view は受信した瞬間に onStartBackWorkspace 経由で消費する。
   */
  prefilledInsightIds?: string[];
  onPrefillConsumed?: () => void;
  /** Phase B: 「💊 処方箋を作る (対話モード)」 button から back workspace を起動。 */
  onStartBackWorkspace?: (insightIds: string[]) => void;
  /** Phase B: 関連 insight filter (insight 詳細から「処方箋を見る」遷移時の絞込)。 */
  relatedInsightFilter?: InsightMinimal | null;
  onClearRelatedInsightFilter?: () => void;
}

export function AnalysisIdeaView({
  districtId,
  uid,
  selfUids,
  nickname: _nickname,
  posts,
  onSelectPost,
  sessions,
  selectedSessionIds,
  aiDialogueSettingsId: _aiDialogueSettingsId,
  prefilledInsightIds,
  onPrefillConsumed,
  onStartBackWorkspace,
  relatedInsightFilter,
  onClearRelatedInsightFilter,
}: AnalysisIdeaViewProps) {
  // Sprint AM: 30s timeout 後の retry 用 key (UI2 無限スピナー解消)
  const [retryKey, setRetryKey] = useState(0);
  const { state: ideasState, deleteIdea } = useEcwIdeas(districtId, retryKey);
  const { state: insightsState } = useEcwInsights(districtId, retryKey);
  const ideasLoadTimedOut = useLoadingTimeout(ideasState.status === 'loading');
  const { toast } = useToast();
  // Task C: outbox (オフライン保留) を 一覧上部に prepend。クリック不可 + 「📤 未送信」 badge。
  const { state: ecwOutboxState } = useEcwOutbox();
  const [onlyMine, setOnlyMine] = useState(false);
  const [detailIdea, setDetailIdea] = useState<IdeaMinimal | null>(null);
  /** Phase B+1.4: チーム filter (旧 IdeaCardList。team が無い idea は除外候補)。 */
  const [teamFilter, setTeamFilter] = useState<string>('');
  /** Phase B+1.4: 写真サムネサイズ slider (32-120)。 */
  const [imageSize, setImageSize] = useState<number>(80);

  // Phase B: 気づき → 処方箋 handoff (受信時に親に back workspace 起動を依頼)。
  const lastPrefillSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (prefilledInsightIds && prefilledInsightIds.length > 0) {
      const sig = prefilledInsightIds.join(',');
      if (lastPrefillSigRef.current !== sig) {
        lastPrefillSigRef.current = sig;
        onStartBackWorkspace?.(prefilledInsightIds);
        onPrefillConsumed?.();
      }
    }
  }, [prefilledInsightIds, onStartBackWorkspace, onPrefillConsumed]);

  // セッション名解決 (カード表示) + 有効セッション ID 集合 (フィルタの孤児判定)。
  const sessionNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sessions ?? []) m.set(s.id, s.name);
    return m;
  }, [sessions]);
  const validSessionIds = useMemo(() => new Set((sessions ?? []).map((s) => s.id)), [sessions]);

  /** Phase B+1.4: 既存 idea の team 一覧 (filter select の option) */
  const availableTeams = useMemo(() => {
    const set = new Set<string>();
    for (const it of ideasState.items) {
      if (it.team && it.team.trim()) set.add(it.team);
    }
    return Array.from(set).sort();
  }, [ideasState.items]);

  // Task C: outbox 由来の idea を IdeaMinimal 互換 shim に変換 (districtId 一致のみ)
  const outboxAsIdeas: IdeaWithOutbox[] = useMemo(
    () =>
      ecwOutboxState.ideas
        .filter((o) => o.districtId === districtId)
        .map((o) => ({
          id: `outbox:${o.id ?? 'unknown'}`,
          districtId: o.districtId,
          userId: o.userId,
          nickname: o.nickname,
          title: o.title,
          observations: o.observations,
          actionIdea: o.actionIdea,
          insightIds: o.insightIds,
          selectedPostIds: o.selectedPostIds,
          hidden: false,
          createdAt: null,
          references: o.references,
          referenceImageBase64: o.referenceImageBase64,
          llmActionQuestions: o.llmActionQuestions,
          llmEffectQuestions: o.llmEffectQuestions,
          llmInsightSummary: o.llmInsightSummary,
          emotionTag: o.emotionTag,
          emotionDescription: o.emotionDescription,
          categoryCode: o.categoryCode,
          categoryLabel: o.categoryLabel,
          team: o.team,
          realName: o.realName,
          sessionId: o.sessionId,
          _pendingOutbox: true,
        })),
    [ecwOutboxState.ideas, districtId],
  );

  // R-70: 「自分」= 現 uid + 端末引き継ぎ元 (selfUids)。省略時は uid 単独 (後方互換)。
  const ownUids = useMemo(
    () => (selfUids && selfUids.length > 0 ? selfUids : uid ? [uid] : []),
    [selfUids, uid],
  );

  const visible = useMemo<IdeaWithOutbox[]>(() => {
    // 投稿のセッションフィルタと同じ規則: 絞込なし(空)は全件、未紐付け/孤児 ID は常に表示。
    const sessionOk = (sid: string | null | undefined): boolean => {
      if (validSessionIds.size === 0 || !selectedSessionIds || selectedSessionIds.size === 0) {
        return true;
      }
      if (!sid) return true;
      if (!validSessionIds.has(sid)) return true;
      return selectedSessionIds.has(sid);
    };
    let list: IdeaWithOutbox[] = ideasState.items.filter((it) => sessionOk(it.sessionId));
    if (onlyMine && ownUids.length > 0) list = list.filter((it) => ownUids.includes(it.userId));
    if (teamFilter) list = list.filter((it) => it.team === teamFilter);
    if (relatedInsightFilter) {
      list = list.filter((it) => it.insightIds.includes(relatedInsightFilter.id));
    }
    // Task C: outbox 由来を先頭に prepend (session / onlyMine / team filter を適用)
    let outboxFiltered: IdeaWithOutbox[] = outboxAsIdeas.filter((o) => sessionOk(o.sessionId));
    if (onlyMine && ownUids.length > 0)
      outboxFiltered = outboxFiltered.filter((o) => ownUids.includes(o.userId));
    if (teamFilter) outboxFiltered = outboxFiltered.filter((o) => o.team === teamFilter);
    if (relatedInsightFilter) {
      outboxFiltered = outboxFiltered.filter((o) => o.insightIds.includes(relatedInsightFilter.id));
    }
    return [...outboxFiltered, ...list];
  }, [
    ideasState.items,
    onlyMine,
    ownUids,
    teamFilter,
    relatedInsightFilter,
    outboxAsIdeas,
    validSessionIds,
    selectedSessionIds,
  ]);

  const availableInsights = insightsState.status === 'ready' ? insightsState.items : [];

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-700">📋 処方箋一覧</h2>
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
            disabled={!uid}
          />
          自分の処方箋のみ表示
        </label>
        {availableTeams.length > 0 && (
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            チーム:
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white"
              aria-label="チームで絞込"
            >
              <option value="">全て</option>
              {availableTeams.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          {/* Phase B+1.4: 画像サイズ slider (旧 IdeaCardList、32-120) */}
          <span>画像:</span>
          <input
            type="range"
            min={32}
            max={120}
            value={imageSize}
            onChange={(e) => setImageSize(Number(e.target.value))}
            className="w-20 cursor-pointer"
            aria-label="処方箋カードの画像サイズ"
          />
          <span className="tabular-nums w-7 text-right">{imageSize}</span>
        </label>
        <span className="text-xs text-slate-500 ml-auto">
          {ideasState.status === 'loading' ? '読込中…' : `${visible.length} 件`}
        </span>
        {/* 処方箋は「気づき」タブで気づきを選び「💊 処方箋をつくる」から作成する一本道のみ。
            投稿/気づきを踏まえないゼロからの作成 (シンプル作成) は廃止 (ユーザー指示)。 */}
      </div>

      {relatedInsightFilter && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
          <span>
            🔗 「{relatedInsightFilter.title}」 をベースにした処方箋 — {visible.length} 件
          </span>
          {onClearRelatedInsightFilter && (
            <button
              type="button"
              onClick={onClearRelatedInsightFilter}
              className="ml-auto text-[11px] underline hover:text-emerald-700"
            >
              絞込を解除
            </button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto p-4">
        {/* Sprint AM: Spinner + 30s timeout 後 retry banner (UI2 無限スピナー解消) */}
        {ideasState.status === 'loading' && !ideasLoadTimedOut && <Spinner label="読込中…" />}
        {ideasState.status === 'loading' && ideasLoadTimedOut && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-warning-700">読込が遅れています。</span>
            <Button variant="secondary" size="sm" onClick={() => setRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}
        {ideasState.status === 'error' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-red-600">読込エラー: {ideasState.error.message}</span>
            <Button variant="secondary" size="sm" onClick={() => setRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}
        {ideasState.status === 'ready' && visible.length === 0 && (
          <p className="text-sm text-slate-500">
            {onlyMine ? '自分の処方箋はまだありません。' : 'まだ処方箋カードがありません。'}
            <br />
            「気づき」タブで気づきを選び、「💊 処方箋をつくる」から作成してください。
          </p>
        )}
        {/*
          画面幅に応じた段組み (2026-09-05): 以前は `space-y-3` の 1 列積みで、カードが
          ブラウザ幅いっぱいに伸び、見出し画像 (`w-full` + 固定高) が極端な横長帯になっていた。
          スマホ 1 列 / タブレット 2 列 / PC 3 列 / 超広画面 4 列。`grid-cols-1` の明示理由は
          気づきカード側と同じ (CLAUDE.md の min-content の落とし穴)。
        */}
        <ul
          className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4"
          data-testid="idea-card-list"
        >
          {visible.map((it) => {
            const isOutbox = it._pendingOutbox === true;
            return (
              <IdeaCard
                key={it.id}
                idea={it}
                isOwner={!isOutbox && ownUids.includes(it.userId)}
                sessionName={it.sessionId ? (sessionNameById.get(it.sessionId) ?? null) : null}
                insights={availableInsights}
                posts={posts}
                imageSize={imageSize}
                pendingOutbox={isOutbox}
                onSelectPost={isOutbox ? undefined : onSelectPost}
                onShowDetail={
                  isOutbox
                    ? () => toast('warning', '未送信のため詳細を表示できません')
                    : () => setDetailIdea(it)
                }
                onDelete={async () => {
                  if (!confirm(`「${it.title}」を削除しますか?`)) return;
                  try {
                    await deleteIdea(it.id);
                    toast('success', '削除しました');
                  } catch (e) {
                    toast('error', `削除失敗: ${(e as Error).message}`);
                  }
                }}
              />
            );
          })}
        </ul>
      </div>

      <IdeaDetailModal
        open={detailIdea !== null}
        onClose={() => setDetailIdea(null)}
        idea={detailIdea}
        posts={posts}
        insights={availableInsights}
        districtId={districtId}
        sessionName={
          detailIdea?.sessionId ? (sessionNameById.get(detailIdea.sessionId) ?? null) : null
        }
        onSelectPost={(p) => {
          setDetailIdea(null);
          onSelectPost?.(p);
        }}
      />

      <p className="px-4 py-2 text-[10px] text-slate-400 shrink-0 border-t border-slate-100">
        ※ Phase B: 処方箋の対話モード作成は「気づき」タブで insight を選んで 「💊 処方箋をつくる
        (N件)」 button から起動します。
      </p>
    </div>
  );
}

interface IdeaCardProps {
  idea: IdeaMinimal;
  isOwner: boolean;
  /** sessionId から解決したセッション名 (内部 ID は出さない)。未解決/無紐付けは null。 */
  sessionName?: string | null;
  insights: InsightMinimal[];
  posts: PostMinimal[];
  /** Phase B+1.4: hero 画像のサイズ (32-120) */
  imageSize: number;
  onSelectPost?: (post: PostMinimal) => void;
  onShowDetail: () => void;
  onDelete: () => Promise<void>;
  /** Task C: outbox 由来 (オフライン未送信)。badge + opacity-70 で識別。 */
  pendingOutbox?: boolean;
}

function IdeaCard({
  idea,
  isOwner,
  sessionName,
  insights,
  posts,
  imageSize,
  onSelectPost,
  onShowDetail,
  onDelete,
  pendingOutbox,
}: IdeaCardProps) {
  const linkedInsights = useMemo(
    () => insights.filter((i) => idea.insightIds.includes(i.id)),
    [insights, idea.insightIds],
  );
  const linkedPosts = useMemo(
    () => posts.filter((p) => idea.selectedPostIds.includes(p.id)),
    [posts, idea.selectedPostIds],
  );
  /** docs/107 Wave8b / old2 L119: hero 画像 = referenceImageBase64 → 先頭 post photo → 📷。 */
  const heroPhotoPath =
    !idea.referenceImageBase64 && linkedPosts[0]?.photoThumbPath
      ? linkedPosts[0].photoThumbPath
      : null;
  // docs/106: doc 保存済 thumb URL (オフライン再表示 fallback)。path がある時だけ採用。
  const heroPhotoUrl = heroPhotoPath ? (linkedPosts[0]?.photoThumbUrl ?? null) : null;
  // old2 L140: hero は先頭投稿、写真 strip は 2 件目以降 (slice(1,5)) + 残数バッジ。
  const stripPosts = useMemo(() => linkedPosts.slice(1, 5), [linkedPosts]);
  const extraCount = Math.max(0, linkedPosts.length - 5);
  const heroHeight = Math.round(imageSize * 1.6);

  return (
    <li
      // docs/107 Wave8b / old2 L117: カード全体クリックで詳細。pendingOutbox は無効。
      className={`rounded-lg border-2 ${
        pendingOutbox
          ? 'border-warning-400 opacity-70'
          : 'border-amber-300 cursor-pointer hover:shadow-md'
      } bg-amber-50 shadow-sm relative overflow-hidden transition-shadow`}
      data-testid="idea-card"
      data-pending-outbox={pendingOutbox ? 'true' : undefined}
      onClick={pendingOutbox ? undefined : onShowDetail}
    >
      {pendingOutbox && (
        <span
          aria-label="未送信"
          className="absolute top-1 left-1 z-[1] text-[10px] bg-warning-300 text-warning-800 px-1.5 py-0.5 rounded"
        >
          📤 未送信
        </span>
      )}
      {/* hero 画像 + 感情/分類 バッジ overlay (old2 L118-131) */}
      <div className="relative bg-slate-100" style={{ minHeight: `${heroHeight}px` }}>
        {idea.referenceImageBase64 ? (
          <img
            src={idea.referenceImageBase64}
            alt={`${idea.title} のキャッチ画像`}
            loading="lazy"
            className="w-full object-contain bg-white"
            style={{ height: `${heroHeight}px` }}
          />
        ) : heroPhotoPath ? (
          <IdeaHeroPhoto
            path={heroPhotoPath}
            url={heroPhotoUrl}
            alt={idea.title}
            height={heroHeight}
          />
        ) : (
          <div
            className="w-full flex items-center justify-center text-3xl text-slate-300"
            style={{ height: `${heroHeight}px` }}
          >
            📷
          </div>
        )}
        {(idea.emotionTag || idea.categoryLabel) && (
          <div className="absolute bottom-1 left-1 flex flex-wrap gap-1">
            {idea.emotionTag && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white shadow">
                {idea.emotionTag}
              </span>
            )}
            {idea.categoryLabel && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-white shadow">
                {idea.categoryLabel}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-slate-800 break-words">{idea.title}</h3>
            {/* old2 L134-137: セッション名(新方式) / 検討チーム(旧 fallback) + nickname を両端揃え。
                セッション名は sessionId から解決、無ければ旧 team、どちらも無ければ —。 */}
            <div className="flex items-center justify-between mt-0.5">
              <p className="text-[11px] text-slate-500">
                {sessionName
                  ? `セッション: ${sessionName}`
                  : idea.team
                    ? `検討チーム: ${idea.team}`
                    : '—'}
              </p>
              <span className="text-[10px] text-slate-400">{idea.nickname || '匿名'}</span>
            </div>
          </div>
          {/* 詳細 button は冗長導線として残す (カード click と同じ)。click 伝播を止める。 */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShowDetail();
            }}
            aria-label="処方箋カード詳細を表示"
            className="text-amber-700 hover:text-amber-900 text-xs px-1.5 py-0.5 rounded border border-amber-400 bg-white transition-colors shrink-0"
          >
            詳細
          </button>
          {isOwner && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void onDelete();
              }}
              aria-label="処方箋カードを削除"
              className="text-slate-400 hover:text-red-600 text-xs px-1.5 py-0.5 rounded transition-colors shrink-0"
            >
              削除
            </button>
          )}
        </div>

        {/* old2 L138: observations 抜粋 */}
        {idea.observations && (
          <p className="text-xs text-slate-700 whitespace-pre-wrap mt-2 line-clamp-3">
            {idea.observations}
          </p>
        )}

        {/* old2 L139-149: 2 件目以降の写真 strip (横スクロール) + 残数バッジ */}
        {stripPosts.length > 0 && (
          <div
            className="mt-2 flex gap-1 overflow-x-auto pb-1"
            aria-label={`関連投稿写真 ${linkedPosts.length} 件`}
          >
            {stripPosts.map((p) => (
              <span
                key={p.id}
                role={onSelectPost ? 'button' : undefined}
                tabIndex={onSelectPost ? 0 : undefined}
                onClick={(e) => {
                  if (!onSelectPost) return;
                  e.stopPropagation();
                  onSelectPost(p);
                }}
                onKeyDown={(e) => {
                  if (onSelectPost && (e.key === 'Enter' || e.key === ' ')) {
                    e.stopPropagation();
                    e.preventDefault();
                    onSelectPost(p);
                  }
                }}
                className="shrink-0"
                title={p.title || '(無題)'}
              >
                <IdeaStripThumb post={p} size={imageSize} />
              </span>
            ))}
            {extraCount > 0 && (
              <span
                className="shrink-0 flex items-center justify-center rounded bg-slate-200 text-slate-600 text-xs font-semibold px-2"
                style={{ height: `${imageSize}px` }}
              >
                +{extraCount}
              </span>
            )}
          </div>
        )}

        {linkedInsights.length > 0 && (
          <p className="text-[10px] text-slate-500 mt-2">
            💡 ベースとした気づき {linkedInsights.length} 件
          </p>
        )}
      </div>
    </li>
  );
}

/* ============================================================================
 * docs/107 Wave8b — カード写真 strip の可変サイズサムネ (old2 L140-147、imageSize 連動)。
 * 横長 (width = max(imageSize*1.3, 40))、写真なしは 📷 placeholder。
 * ========================================================================== */

function IdeaStripThumb({ post, size }: { post: PostMinimal; size: number }) {
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl);
  const width = Math.max(Math.round(size * 1.3), 40);
  const style = { width: `${width}px`, height: `${size}px` } as React.CSSProperties;
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className="object-cover rounded border border-amber-200 bg-white"
        style={style}
      />
    );
  }
  return (
    <span
      aria-label={photo.status === 'failed' ? '写真の読込に失敗' : '写真読込中'}
      className={`flex items-center justify-center rounded border border-amber-200 bg-white text-slate-400 text-sm ${photo.status === 'loading' ? 'animate-pulse' : ''}`}
      style={style}
    >
      📷
    </span>
  );
}

/* ============================================================================
 * Phase B+1.4 — Idea カードの hero 画像 (post photo を usePhotoUrl 経由で取得)。
 *
 * referenceImageBase64 が無い idea でも先頭の linkedPost.photoThumbPath を hero として
 * 表示するための helper。loading 中は skeleton、失敗時は placeholder。
 * ========================================================================== */

function IdeaHeroPhoto({
  path,
  url,
  alt,
  height,
}: {
  path: string;
  /** doc 保存済 thumb URL (docs/106、楽観表示 + オフライン fallback)。 */
  url?: string | null;
  alt: string;
  height: number;
}) {
  const photo = usePhotoUrl(path, url);
  const style = { maxHeight: `${height}px` } as React.CSSProperties;
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={alt}
        loading="lazy"
        className="w-full object-cover rounded mb-2 bg-white border border-amber-200"
        style={style}
      />
    );
  }
  return (
    <div
      className={`w-full rounded mb-2 bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 text-sm ${photo.status === 'loading' ? 'animate-pulse' : ''}`}
      style={{ height: `${Math.round(height * 0.6)}px` }}
      aria-label={photo.status === 'failed' ? 'キャッチ画像読込失敗' : 'キャッチ画像読込中'}
    >
      📷
    </div>
  );
}
